import React from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Card } from "antd";
import { useSessionsByUserId } from "../../api/SessionsAPI";
import { useNavigate } from "react-router-dom";
import {
  agGridThemeVars,
  agGridDefaultColDef,
  agGridPinnedActionsCellStyle,
} from "../../constants/agGridTheme";
import { StatusTag } from "../common/SessionTags";

const RubricSessionsTable = ({ userId }) => {
  const navigate = useNavigate();
  const {
    data: sessions,
    isLoading: sessionsLoading,
    error: sessionsError,
  } = useSessionsByUserId(userId, {
    status: "completed",
    includeCompletedCount: true,
    includeRubricCount: true,
  });

  const generateRubricRenderer = (params) => {
    const remainingQuota = 3 - params.data.rubric_count;
    const isDisabled = remainingQuota <= 0;

    return (
      <button
        onClick={() =>
          !isDisabled && navigate(`/rubric-generator/${params.data.id}`)
        }
        className={`${
          isDisabled
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-primary hover:bg-primaryLight text-white"
        } text-xs font-medium py-1.5 px-3 rounded h-fit leading-none`}
        disabled={isDisabled}
        title={isDisabled ? "No generations remaining" : "Generate a rubric"}
      >
        Generate Rubric
      </button>
    );
  };

  const statusRenderer = (params) => <StatusTag status={params.value} />;

  const columnDefs = [
    {
      headerName: "Name",
      field: "name",
      minWidth: 200,
      flex: 1,
      valueGetter: (params) =>
        params.data?.name || params.data?.display_name || "Untitled Session",
    },
    { headerName: "ID", field: "id", width: 90 },
    {
      headerName: "Started At",
      field: "created_at",
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
      width: 160,
    },
    { headerName: "Status", field: "status", width: 120, cellRenderer: statusRenderer },
    { headerName: "Competencies Achieved", field: "completed_count", width: 150 },
    {
      headerName: "Remaining Rubric Quota",
      width: 150,
      valueGetter: (params) => Math.max(0, 3 - params.data.rubric_count),
      cellRenderer: (params) => {
        const value = params.value;
        return (
          <span className={value === 0 ? "text-red-500 font-medium" : ""}>
            {value}
          </span>
        );
      },
    },
    {
      headerName: "Actions",
      cellRenderer: generateRubricRenderer,
      sortable: false,
      filter: false,
      pinned: "right",
      width: 170,
      cellStyle: agGridPinnedActionsCellStyle,
    },
  ];

  if (sessionsLoading) {
    return <div>Loading...</div>;
  }

  if (sessionsError) {
    return <div>Error loading sessions: {sessionsError.message}</div>;
  }

  return (
    <Card className="w-full">
      <div
        className="ag-theme-alpine"
        style={{
          height: 400,
          width: "100%",
          ...agGridThemeVars,
        }}
      >
        <AgGridReact
          rowData={sessions}
          columnDefs={columnDefs}
          defaultColDef={agGridDefaultColDef}
          pagination={true}
          paginationPageSize={10}
          domLayout="autoHeight"
        />
      </div>
    </Card>
  );
};

export default RubricSessionsTable;